// Validation utilities
import categoriesService from '../services/categoriesService.js';

const createValidationSystem = () => {

  // Basic validation rules
  const rules = {
    required: (value) => value !== null && value !== undefined && value.toString().trim() !== '',

    email: (value) => {
      if (!value) return true // Optional field
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(value)
    },

    url: (value) => {
      if (!value) return true // Optional field
      try {
        new URL(value)
        return true
      } catch {
        return false
      }
    },

    phone: (value) => {
      if (!value) return true // Optional field
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/
      return phoneRegex.test(value.replace(/\s/g, ''))
    },

    minLength: (min) => (value) => {
      if (!value) return true
      return value.length >= min
    },

    maxLength: (max) => (value) => {
      if (!value) return true
      return value.length <= max
    },

    pattern: (regex) => (value) => {
      if (!value) return true
      return regex.test(value)
    },

    oneOf: (options) => (value) => {
      if (!value) return true
      return options.includes(value)
    }
  }

  // WordPress configuration validation
  const validateWordPressConfig = (config) => {
    const errors = [];

    // URL
    if (!rules.required(config.wpUrl)) {
      errors.push('URL WordPress è obbligatorio');
    } else if (!rules.url(config.wpUrl)) {
      errors.push('URL WordPress non valido');
    } else if (!config.wpUrl.startsWith('http')) {
      errors.push('URL WordPress deve iniziare con http:// o https://');
    }

    // Modalità credenziali:
    // - Application Password => username + password
    // - Oppure Token Bearer  => token
    const hasPasswordFlow = !!config.password;
    const hasBearerFlow = !!config.token;

    if (!hasPasswordFlow && !hasBearerFlow) {
      errors.push('Fornisci Application Password (username + password) oppure un Token (Bearer).');
    }

    if (hasPasswordFlow) {
      if (!rules.required(config.username)) {
        errors.push('Username è obbligatorio (per Application Password)');
      } else if (config.username.length < 2) {
        errors.push('Username deve essere di almeno 2 caratteri');
      }
      if (!rules.required(config.password)) {
        errors.push('Password è obbligatoria (per Application Password)');
      } else if (config.password.length < 10) {
        errors.push('Application Password deve essere di almeno 10 caratteri');
      }
    }

    if (hasBearerFlow) {
      if (!rules.required(config.token)) {
        errors.push('Token è obbligatorio (per Bearer)');
      } else if (config.token.length < 20) {
        errors.push('Token sembra troppo corto');
      }
    }

    return { isValid: errors.length === 0, errors };
  };


  // Activity data validation
  const validateActivityData = (data) => {
    const errors = []

    // Required fields
    if (!rules.required(data.nome)) {
      errors.push('Nome attività è obbligatorio')
    } else if (data.nome.length < 2) {
      errors.push('Nome attività deve essere di almeno 2 caratteri')
    } else if (data.nome.length > 100) {
      errors.push('Nome attività non può superare 100 caratteri')
    }

    if (!rules.required(data.categoria)) {
      errors.push('Categoria è obbligatoria')
    } else if (!rules.oneOf(['mangiare', 'dormire', 'comprare', 'vedere', 'associazioni'])(data.categoria)) {
      errors.push('Categoria non valida')
    }

    if (!rules.required(data.descrizione)) {
      errors.push('Descrizione è obbligatoria')
    } else if (data.descrizione.length < 10) {
      errors.push('Descrizione deve essere di almeno 10 caratteri')
    } else if (data.descrizione.length > 2000) {
      errors.push('Descrizione non può superare 2000 caratteri')
    }

    // Optional fields validation
    if (data.telefono && !rules.phone(data.telefono)) {
      errors.push('Numero di telefono non valido')
    }

    if (data.email && !rules.email(data.email)) {
      errors.push('Email non valida')
    }

    if (data.sito_web && !rules.url(data.sito_web)) {
      errors.push('URL sito web non valido')
    }

    if (data.facebook && !rules.url(data.facebook)) {
      errors.push('URL Facebook non valido')
    }

    // Instagram è testo libero (es. @nomeutente): nessuna validazione URL

    if (data.immagine_principale && !rules.url(data.immagine_principale)) {
      errors.push('URL immagine principale non valido')
    }

    // Validate gallery URLs if provided
    if (data.galleria) {
      const galleryUrls = data.galleria.split('\n').filter(url => url.trim())
      const invalidUrls = galleryUrls.filter(url => !rules.url(url.trim()))

      if (invalidUrls.length > 0) {
        errors.push(`URL galleria non validi: ${invalidUrls.join(', ')}`)
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Translation data validation
  const validateTranslationData = (data) => {
    const errors = []

    // Required fields for translation
    if (!rules.required(data.nome)) {
      errors.push('Nome attività (EN) è obbligatorio')
    } else if (data.nome.length < 2) {
      errors.push('Nome attività (EN) deve essere di almeno 2 caratteri')
    } else if (data.nome.length > 100) {
      errors.push('Nome attività (EN) non può superare 100 caratteri')
    }

    if (!rules.required(data.descrizione)) {
      errors.push('Descrizione (EN) è obbligatoria')
    } else if (data.descrizione.length < 10) {
      errors.push('Descrizione (EN) deve essere di almeno 10 caratteri')
    } else if (data.descrizione.length > 2000) {
      errors.push('Descrizione (EN) non può superare 2000 caratteri')
    }

    // Orari is optional for translations
    if (data.orari && data.orari.length > 500) {
      errors.push('Orari (EN) non può superare 500 caratteri')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Real-time field validation
  const validateField = (fieldName, value, activityData = {}) => {
    const errors = []

    switch (fieldName) {
      case 'nome':
        if (!rules.required(value)) {
          errors.push('Nome è obbligatorio')
        } else if (value.length < 2) {
          errors.push('Minimo 2 caratteri')
        } else if (value.length > 100) {
          errors.push('Massimo 100 caratteri')
        }
        break

      case 'categoria':
        if (!rules.required(value)) {
          errors.push('Categoria è obbligatoria')
        } else if (!rules.oneOf(['mangiare', 'dormire', 'comprare', 'vedere', 'associazioni'])(value)) {
          errors.push('Categoria non valida')
        }
        break

      case 'descrizione':
        if (!rules.required(value)) {
          errors.push('Descrizione è obbligatoria')
        } else if (value.length < 10) {
          errors.push('Minimo 10 caratteri')
        } else if (value.length > 2000) {
          errors.push('Massimo 2000 caratteri')
        }
        break

      case 'telefono':
        if (value && !rules.phone(value)) {
          errors.push('Formato telefono non valido')
        }
        break

      case 'email':
        if (value && !rules.email(value)) {
          errors.push('Email non valida')
        }
        break

      case 'sito_web':
        if (value && !rules.url(value)) {
          errors.push('URL non valido')
        }
        break

      case 'immagine_principale':
        if (value && !rules.url(value)) {
          errors.push('URL immagine non valido')
        }
        break

      default:
        break
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Sanitization functions
  const sanitize = {
    text: (value) => {
      if (!value) return ''
      return value.toString().trim().replace(/[<>]/g, '')
    },

    email: (value) => {
      if (!value) return ''
      return value.toString().toLowerCase().trim()
    },

    url: (value) => {
      if (!value) return ''
      let url = value.toString().trim()
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url
      }
      return url
    },

    phone: (value) => {
      if (!value) return ''
      return value.toString().replace(/[^\d\+\-\s\(\)]/g, '').trim()
    },

    multiline: (value) => {
      if (!value) return ''
      return value.toString().trim().replace(/[<>]/g, '')
    }
  }

  // Sanitize activity data
  const sanitizeActivityData = (data) => {
    return {
      nome: sanitize.text(data.nome),
      categoria: sanitize.text(data.categoria),
      descrizione: sanitize.multiline(data.descrizione),
      telefono: sanitize.phone(data.telefono),
      email: sanitize.email(data.email),
      indirizzo: sanitize.text(data.indirizzo),
      sito_web: sanitize.url(data.sito_web),
      facebook: sanitize.url(data.facebook),
      instagram: sanitize.text(data.instagram),
      orari: sanitize.multiline(data.orari),
      immagine_principale: sanitize.url(data.immagine_principale),
      galleria: sanitize.multiline(data.galleria)
    }
  }

  // Form validation helpers
  const validateForm = (formElement, validationRules = {}) => {
    const formData = new FormData(formElement)
    const data = Object.fromEntries(formData.entries())
    const errors = {}
    let isValid = true

    Object.entries(validationRules).forEach(([fieldName, fieldRules]) => {
      const fieldValue = data[fieldName]
      const fieldErrors = []

      fieldRules.forEach(rule => {
        if (typeof rule === 'function') {
          if (!rule(fieldValue)) {
            fieldErrors.push('Valore non valido')
          }
        } else if (typeof rule === 'object') {
          if (!rules[rule.type]) {
            console.warn(`Unknown validation rule: ${rule.type}`)
            return
          }

          const validator = rule.params ? rules[rule.type](...rule.params) : rules[rule.type]
          if (!validator(fieldValue)) {
            fieldErrors.push(rule.message || 'Valore non valido')
          }
        }
      })

      if (fieldErrors.length > 0) {
        errors[fieldName] = fieldErrors
        isValid = false
      }
    })

    return {
      isValid,
      errors,
      data: sanitizeActivityData(data)
    }
  }

  // Password strength validation
  const validatePasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    }

    const score = Object.values(checks).filter(Boolean).length

    let strength = 'weak'
    if (score >= 4) strength = 'strong'
    else if (score >= 3) strength = 'medium'

    return {
      strength,
      score,
      checks,
      isValid: score >= 3
    }
  }

  // File validation
  const validateFile = (file, options = {}) => {
    const errors = []

    const maxSize = options.maxSize || 5 * 1024 * 1024 // 5MB default
    const allowedTypes = options.allowedTypes || ['image/jpeg', 'image/png', 'image/webp']

    if (file.size > maxSize) {
      errors.push(`File troppo grande. Massimo ${Math.round(maxSize / 1024 / 1024)}MB`)
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push(`Tipo file non supportato. Supportati: ${allowedTypes.join(', ')}`)
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Bulk validation
  const validateBulkData = (dataArray, validator) => {
    const results = dataArray.map((item, index) => ({
      index,
      ...validator(item)
    }))

    const validItems = results.filter(r => r.isValid)
    const invalidItems = results.filter(r => !r.isValid)

    return {
      total: dataArray.length,
      valid: validItems.length,
      invalid: invalidItems.length,
      results,
      validItems: validItems.map(r => dataArray[r.index]),
      invalidItems: invalidItems.map(r => ({ index: r.index, data: dataArray[r.index], errors: r.errors }))
    }
  }

  // --- Helpers per payload backend -----------------------------------------

  // Converte una stringa multilinea in array pulito
  const toArrayFromMultiline = (v) =>
    (v || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

  /**
   * Normalizza i dati dell'attività nel formato WordPress REST API
   */
  const normalizeActivityForBackend = (data) => {
    const clean = sanitizeActivityData(data);

    const payload = {
      title: clean.nome,
      content: clean.descrizione,
      status: 'publish',
      meta: {
        _attivita_telefono: clean.telefono || '',
        _attivita_email: clean.email || '',
        _attivita_indirizzo: clean.indirizzo || '',
        _attivita_sito_web: clean.sito_web || '',
        _attivita_facebook: clean.facebook || '',
        _attivita_instagram: clean.instagram || '',
        _attivita_orari: clean.orari || '',
      }
    };

    // Add categoria_attivita taxonomy if present - use dynamic mapping from categoriesService
    if (clean.categoria) {
      const categoryId = categoriesService.getCategoryId(clean.categoria);
      if (categoryId) {
        payload.categoria_attivita = [categoryId];
      }
    }

    // Add featured_media if present (0 is valid: it detaches the featured image)
    if (data.featured_media_id !== undefined && data.featured_media_id !== null) {
      payload.featured_media = data.featured_media_id;
    }

    // Add gallery meta if provided ('' clears the whole gallery)
    if (data.galleria !== undefined) {
      const galleryUrls = toArrayFromMultiline(clean.galleria);
      payload.meta._attivita_galleria = galleryUrls.join(',');
    }

    // Add gallery media IDs if present
    if (data.gallery_media_ids && Array.isArray(data.gallery_media_ids)) {
      payload.meta._attivita_gallery_ids = data.gallery_media_ids.join(',');
    }

    return payload;
  };

  /**
   * Accetta sia Application Password (username+password) sia token Bearer.
   * - Se passi password => usa Basic Auth verso WP
   * - Se passi token   => usa Bearer
   * Al backend inviamo comunque un singolo "secret".
   */
  const normalizeWpCredentialForBackend = (config) => {
    const wpUrl = sanitize.url(config.wpUrl);
    const username = sanitize.text(config.username);
    const password = sanitize.text(config.password);
    const token = sanitize.text(config.token);     // opzionale, se usi OAuth/JWT
    const label = sanitize.text(config.label);     // es. "Blog personale"

    // decidiamo la modalità: Basic (app password) oppure Bearer
    const useBearer = !!token && !password;

    return {
      siteUrl: wpUrl,
      account: useBearer ? null : username || null,   // per Basic serve username
      secret: useBearer ? token : password,           // il "segreto" da cifrare a backend
      tokenType: useBearer ? 'Bearer' : null,
      label: label || null,
      // expiresAt: null // opzionale se usi OAuth
    };
  };


  return {
    // Core validation
    rules,
    validateWordPressConfig,
    validateActivityData,
    validateTranslationData,
    validateField,
    validateForm,
    validatePasswordStrength,
    validateFile,
    validateBulkData,

    // Sanitization
    sanitize,
    sanitizeActivityData,

    // 🔹 nuove utilità per il backend
    normalizeActivityForBackend,
    normalizeWpCredentialForBackend,
  }
}

export const validation = createValidationSystem()